import {
  themeBackgroundMap,
  themeBorderColorMap,
  themeGlowMap,
  themeHoverBackgroundMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import {
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
  forwardRef,
} from 'react'
import { type TestIds, getTestProps } from '../../utils/testing.ts'
import { Icon } from '../icon/icon.tsx'
import { Text } from '../text/text.tsx'

export type ChipVariant = 'pill' | 'rounded-square' | 'square'

const chipVariantMap: Record<ChipVariant, string> = {
  pill: 'rounded-full',
  'rounded-square': 'rounded-sm',
  square: 'rounded-none',
}

type ChipProps = Readonly<
  PropsWithChildren<
    TestIds & {
      className?: string
      dismissible?: boolean
      disabled?: boolean
      id?: string
      onDismissClick?: (e: MouseEvent<HTMLButtonElement>) => void
      onSelected?: (selected: boolean) => void
      selectable?: boolean
      selected?: boolean
      title?: string
      variant?: ChipVariant
    }
  >
>

const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      children,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      dismissible,
      id,
      onDismissClick,
      onSelected,
      selectable,
      selected,
      title,
      variant = 'pill',
      ...props
    }: ChipProps,
    ref
  ) => {
    const handleSelected = () => {
      if (onSelected) {
        onSelected(!selected)
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' && onSelected) {
        onSelected(!selected)
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex w-fit gap-2 h-8 justify-center items-center px-4 flex-none border-2 border-transparent',
          chipVariantMap[variant],
          {
            'pr-3': dismissible,
            [cn(
              themeBackgroundMap['info-muted'],
              themeBorderColorMap.info,
              themeGlowMap.info
            )]: selected,
            [`${themeBackgroundMap.lighten}`]: !selected,
            [`${themeHoverBackgroundMap.info} cursor-pointer`]:
              selectable && !selected,
            'cursor-pointer': selectable,
            className,
          }
        )}
        onClick={selectable ? handleSelected : undefined}
        onKeyDown={selectable ? handleKeyDown : undefined}
        data-id={id}
        title={title}
        {...getTestProps({ dataTestId, dataTrackingId })}
        {...props}
      >
        <Text
          fontSize="sm"
          className="leading-normal line-clamp-1"
          textColor={disabled ? 'disabled' : 'primary'}
        >
          {children}
        </Text>
        {dismissible && (
          <button
            type="button"
            onClick={onDismissClick}
            className={cn(
              'p-0.5 -m-0.5 rounded-full',
              themeHoverBackgroundMap.lighten
            )}
          >
            <Icon name="XMarkIcon" size="sm" color="tertiary" />
          </button>
        )}
      </div>
    )
  }
)

export { Chip, type ChipProps }
